import React, { useState, useEffect } from "react";
import axios from "axios";
import Fuse from "fuse.js";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, User, Calendar, BookOpen, ChevronDown, Search, Sparkles } from "lucide-react";
import Loader from "../status_pages/Loader";
import "../../css/People.css";

export default function People() {
  const [userdata, setUserdata] = useState([]);
  const [nameFilter, setNameFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);

  const fetchUserdata = async () => {
    try {
      setLoading(true);
      console.log("Fetching Userdata...");
      const res = await axios.get(`${process.env.REACT_APP_API}/GetAllUsers`);
      console.log("Response:", res.data);
      setUserdata(res.data);
    } catch (error) {
      console.error("Error fetching Userdata:", error);
      setError("Error fetching user data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserdata();
  }, []);

  // Track scroll position to make header sticky
  useEffect(() => {
    const handleScroll = () => {
      const tableContainer = document.getElementById('table-container');
      if (tableContainer) {
        const rect = tableContainer.getBoundingClientRect();
        setIsHeaderSticky(rect.top <= 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.year-filter-container')) {
        setIsYearDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fuse = new Fuse(userdata, {
    keys: ["Name"],
    threshold: 0.3,
    findAllMatches: true,
  });

  let filteredData = userdata;

  if (nameFilter) {
    const result = fuse.search(nameFilter);
    filteredData = result.map((r) => r.item);
  }

  if (yearFilter) {
    filteredData = filteredData.filter((item) =>
      (item.Grad_Year?.toString() || "").includes(yearFilter)
    );
  }

  const yearOptions = ["2024", "2025", "2026", "2027", "2028", "2029"];

  const handleYearSelect = (year) => {
    setYearFilter(year);
    setIsYearDropdownOpen(false);
  };

  const clearYearFilter = () => {
    setYearFilter("");
    setIsYearDropdownOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1a0b2e 0%, #2d1b4e 50%, #4a2c6d 100%)' }}>
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1a0b2e 0%, #2d1b4e 50%, #4a2c6d 100%)' }}>
        <div className="text-red-400 text-xl bg-red-950/50 px-8 py-4 rounded-2xl border border-red-500/30 backdrop-blur-sm">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a0b2e 0%, #2d1b4e 50%, #4a2c6d 100%)' }}>
      {/* Animated Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.3, 0.2],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(139, 92, 246, 0.3) 0%, transparent 70%)' }}
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.15, 0.25, 0.15],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(168, 85, 247, 0.3) 0%, transparent 70%)' }}
        />
      </div>

      <div className="flex-grow flex flex-col relative z-10 text-purple-100">
        <motion.header
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="backdrop-blur-xl py-6 px-4 sm:px-6 lg:px-8 shadow-2xl border-b"
          style={{
            background: 'linear-gradient(90deg, rgba(26, 11, 46, 0.8) 0%, rgba(45, 27, 78, 0.8) 50%, rgba(26, 11, 46, 0.8) 100%)',
            borderColor: 'rgba(139, 92, 246, 0.3)'
          }}
        >
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center">
            <div className="flex items-center gap-4 mb-4 sm:mb-0">
              <motion.div
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <div className="absolute inset-0 rounded-full blur-lg opacity-50" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)' }}></div>
                <GraduationCap className="relative" size={40} style={{ color: '#e9d5ff' }} />
              </motion.div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold" style={{ color: '#f3e8ff' }}>
                  Student Information
                </h1>
                <p className="text-sm mt-1" style={{ color: '#c4b5fd' }}>Discover and connect with students</p>
              </div>
            </div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="backdrop-blur-sm px-6 py-3 rounded-full border flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(168, 85, 247, 0.2) 100%)',
                borderColor: 'rgba(196, 181, 253, 0.3)'
              }}
            >
              <Sparkles size={20} style={{ color: '#e9d5ff' }} />
              <span className="font-medium" style={{ color: '#f3e8ff' }}>{filteredData.length} Students</span>
            </motion.div>
          </div>
        </motion.header>

        <main className="flex-grow flex flex-col font-libre p-4 sm:p-6 lg:p-8">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="max-w-7xl w-full mx-auto mb-8 flex flex-col sm:flex-row gap-4"
          >
            {/* Name Filter */}
            <div className="relative flex-1 group">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="relative"
              >
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  className="w-full pl-12 pr-12 py-4 backdrop-blur-md border-2 rounded-2xl focus:outline-none focus:ring-4 hover:shadow-2xl transition-all duration-300 shadow-xl transform hover:-translate-y-1"
                  style={{
                    background: 'linear-gradient(135deg, rgba(45, 27, 78, 0.6) 0%, rgba(74, 44, 109, 0.6) 100%)',
                    borderColor: 'rgba(139, 92, 246, 0.4)',
                    color: '#f3e8ff',
                    boxShadow: '0 10px 40px rgba(139, 92, 246, 0.2)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(196, 181, 253, 0.6)';
                    e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.3), 0 10px 40px rgba(139, 92, 246, 0.3)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(139, 92, 246, 0.4)';
                    e.target.style.boxShadow = '0 10px 40px rgba(139, 92, 246, 0.2)';
                  }}
                />
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 group-hover:scale-110 transition-all duration-300" size={20} style={{ color: '#c4b5fd' }} />
                {nameFilter && (
                  <motion.button
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    whileHover={{ scale: 1.2, rotate: 90 }}
                    onClick={() => setNameFilter("")}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 transition-all duration-200 text-2xl"
                    style={{ color: '#c4b5fd' }}
                  >
                    ×
                  </motion.button>
                )}
              </motion.div>
            </div>

            {/* Year Filter with Dropdown */}
            <div className="year-filter-container relative flex-1 group">
              <motion.button
                whileHover={{ scale: 1.02 }}
                onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
                className="w-full pl-12 pr-12 py-4 backdrop-blur-md border-2 rounded-2xl focus:outline-none focus:ring-4 transition-all duration-300 shadow-xl flex items-center justify-between transform hover:-translate-y-1"
                style={{
                  background: yearFilter
                    ? 'linear-gradient(135deg, rgba(74, 44, 109, 0.7) 0%, rgba(45, 27, 78, 0.7) 100%)'
                    : 'linear-gradient(135deg, rgba(45, 27, 78, 0.6) 0%, rgba(74, 44, 109, 0.6) 100%)',
                  borderColor: yearFilter ? 'rgba(196, 181, 253, 0.5)' : 'rgba(139, 92, 246, 0.4)',
                  color: '#f3e8ff',
                  boxShadow: yearFilter
                    ? '0 10px 40px rgba(196, 181, 253, 0.3)'
                    : '0 10px 40px rgba(139, 92, 246, 0.2)'
                }}
              >
                <div className="flex items-center">
                  <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 group-hover:scale-110 transition-all duration-300" size={20} style={{ color: '#c4b5fd' }} />
                  <span className={yearFilter ? "font-medium" : ""} style={{ color: yearFilter ? '#f3e8ff' : '#c4b5fd' }}>
                    {yearFilter || "Filter by year"}
                  </span>
                </div>
                <motion.div
                  animate={{ rotate: isYearDropdownOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown
                    className="group-hover:scale-110 transition-all duration-300"
                    size={20}
                    style={{ color: '#c4b5fd' }}
                  />
                </motion.div>
              </motion.button>

              {/* Dropdown Menu */}
              <AnimatePresence>
                {isYearDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -20, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.9 }}
                    transition={{ duration: 0.3, type: "spring", damping: 25 }}
                    className="absolute top-full left-0 right-0 mt-3 backdrop-blur-xl border-2 rounded-2xl shadow-2xl z-50 overflow-hidden"
                    style={{
                      background: 'linear-gradient(180deg, rgba(45, 27, 78, 0.95) 0%, rgba(74, 44, 109, 0.95) 100%)',
                      borderColor: 'rgba(139, 92, 246, 0.4)',
                      boxShadow: '0 20px 60px rgba(139, 92, 246, 0.3)'
                    }}
                  >
                    {/* Clear Filter Option */}
                    <motion.button
                      whileHover={{ x: 4 }}
                      onClick={clearYearFilter}
                      className="w-full px-5 py-4 text-left transition-all duration-200 border-b flex items-center justify-between group/clear"
                      style={{
                        color: '#e9d5ff',
                        borderColor: 'rgba(139, 92, 246, 0.3)'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <span className="font-medium">All years</span>
                      <span className="text-xs px-3 py-1 rounded-full transition-colors duration-200" style={{ background: 'rgba(139, 92, 246, 0.3)' }}>
                        Clear
                      </span>
                    </motion.button>

                    {/* Year Options */}
                    {yearOptions.map((year, idx) => (
                      <motion.button
                        key={year}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        whileHover={{ x: 4 }}
                        onClick={() => handleYearSelect(year)}
                        className="w-full px-5 py-4 text-left transition-all duration-200 border-b last:border-b-0"
                        style={{
                          background: yearFilter === year
                            ? 'linear-gradient(90deg, rgba(139, 92, 246, 0.3) 0%, rgba(168, 85, 247, 0.3) 100%)'
                            : 'transparent',
                          color: yearFilter === year ? '#f3e8ff' : '#e9d5ff',
                          borderColor: 'rgba(139, 92, 246, 0.3)',
                          fontWeight: yearFilter === year ? '600' : '400'
                        }}
                        onMouseEnter={(e) => {
                          if (yearFilter !== year) e.currentTarget.style.background = 'rgba(139, 92, 246, 0.15)'
                        }}
                        onMouseLeave={(e) => {
                          if (yearFilter !== year) e.currentTarget.style.background = 'transparent'
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <span>{year}</span>
                          {yearFilter === year && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-2.5 h-2.5 rounded-full shadow-lg"
                              style={{
                                background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                                boxShadow: '0 0 10px rgba(139, 92, 246, 0.5)'
                              }}
                            />
                          )}
                        </div>
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          <motion.div
            id="table-container"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex-grow backdrop-blur-xl border-2 rounded-3xl shadow-2xl max-w-7xl w-full mx-auto overflow-hidden"
            style={{
              background: 'linear-gradient(180deg, rgba(45, 27, 78, 0.4) 0%, rgba(74, 44, 109, 0.4) 100%)',
              borderColor: 'rgba(139, 92, 246, 0.3)'
            }}
          >
            {/* Sticky Header - Positioned outside scroll container */}
            <div
              className={`${isHeaderSticky ? 'fixed top-0 z-50' : 'relative'
                } transition-all duration-300`}
              style={isHeaderSticky ? {
                width: 'calc(100% - 2rem)',
                maxWidth: '80rem',
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '0 1rem'
              } : {}}
            >
              <div className={`${isHeaderSticky ? 'border-2 rounded-3xl shadow-2xl backdrop-blur-xl' : ''} overflow-x-auto`} style={isHeaderSticky ? { borderColor: 'rgba(139, 92, 246, 0.3)' } : {}}>
                <table className="w-full text-left" style={{ minWidth: '600px' }}>
                  <thead className="backdrop-blur-sm" style={{ background: 'linear-gradient(90deg, rgba(139, 92, 246, 0.6) 0%, rgba(168, 85, 247, 0.6) 100%)', color: '#f3e8ff' }}>
                    <tr>
                      <th className="font-bold p-5 sm:p-6 border-b-2" style={{ width: '10%', borderColor: 'rgba(196, 181, 253, 0.3)' }}>
                        <span className="text-sm uppercase tracking-wider">S.No</span>
                      </th>
                      <th className="font-bold p-5 sm:p-6 border-b-2" style={{ width: '35%', borderColor: 'rgba(196, 181, 253, 0.3)' }}>
                        <span className="text-sm uppercase tracking-wider">Name</span>
                      </th>
                      <th className="font-bold p-5 sm:p-6 border-b-2" style={{ width: '20%', borderColor: 'rgba(196, 181, 253, 0.3)' }}>
                        <span className="text-sm uppercase tracking-wider">Year</span>
                      </th>
                      <th className="font-bold p-5 sm:p-6 border-b-2" style={{ width: '35%', borderColor: 'rgba(196, 181, 253, 0.3)' }}>
                        <span className="text-sm uppercase tracking-wider">Degree</span>
                      </th>
                    </tr>
                  </thead>
                </table>
              </div>
            </div>

            {/* Scrollable Body */}
            <div className="overflow-x-auto" style={isHeaderSticky ? { marginTop: '81px' } : {}}>
              <table className="w-full text-left" style={{ minWidth: '600px' }}>
                <tbody>
                  <AnimatePresence>
                    {filteredData.map((item, index) => (
                      <motion.tr
                        key={item.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3, delay: index * 0.03 }}
                        whileHover={{ x: 4 }}
                        className="backdrop-blur-sm border-b transition-all duration-300 cursor-pointer"
                        style={{
                          background: 'rgba(26, 11, 46, 0.3)',
                          borderColor: 'rgba(139, 92, 246, 0.2)'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.15)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(26, 11, 46, 0.3)'}
                      >
                        <td className="p-5 sm:p-6 font-semibold" style={{ width: '10%', color: '#c4b5fd' }}>
                          <motion.div
                            whileHover={{ scale: 1.1 }}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full"
                            style={{ background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(168, 85, 247, 0.3) 100%)' }}
                          >
                            {index + 1}
                          </motion.div>
                        </td>
                        {/* <td className="p-5 sm:p-6" style={{ width: '35%', color: '#f3e8ff' }}>
                          <Link 
                            to={`/profile/${item._id}`} 
                            className="transition-all duration-300 font-medium inline-flex items-center gap-2 group"
                            style={{ color: '#c4b5fd' }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#e9d5ff'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#c4b5fd'}
                          >
                            <motion.span whileHover={{ scale: 1.05 }}>
                              {item.Name}
                            </motion.span>
                            <motion.span
                              initial={{ opacity: 0, x: -5 }}
                              whileHover={{ opacity: 1, x: 0 }}
                            >
                              →
                            </motion.span>
                          </Link>
                        </td> */}

                        <td className="p-5 sm:p-6" style={{ width: '35%', color: '#f3e8ff' }}>
                          <span
                            className="transition-all duration-300 font-medium inline-flex items-center gap-2 group"
                            style={{ color: '#c4b5fd' }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#e9d5ff'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#c4b5fd'}
                          >
                            <motion.span whileHover={{ scale: 1.05 }}>
                              {item.Name}
                            </motion.span>

                            <motion.span
                              initial={{ opacity: 0, x: -5 }}
                              whileHover={{ opacity: 1, x: 0 }}
                            >
                              →
                            </motion.span>
                          </span>
                        </td>
                        <td className="p-5 sm:p-6 font-medium" style={{ width: '20%', color: '#e9d5ff' }}>
                          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm" style={{ background: 'rgba(139, 92, 246, 0.2)' }}>
                            <Calendar size={14} style={{ color: '#c4b5fd' }} />
                            {item.Grad_Year}
                          </span>
                        </td>
                        <td className="p-5 sm:p-6 flex items-center" style={{ width: '35%', color: '#e9d5ff' }}>
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            className="flex items-center gap-2"
                          >
                            <BookOpen className="flex-shrink-0" size={18} style={{ color: '#a78bfa' }} />
                            <span className="truncate">{item.Degree}</span>
                          </motion.div>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </motion.div>
        </main>
      </div>
    </div>
  );
}